/**
 * I'm not 100% convinced with this manager... this is the management of the flow just for the UI layer
 * this is why it has dependencies on external packages like dagre and xyflow, that are used for rendering
 */
import Dagre from '@dagrejs/dagre'
import { ButtonPropsColorOverrides } from '@mui/material'
import * as colors from '@mui/material/colors'
import { OverridableStringUnion } from '@mui/types'
import { NodeAddChange, NodePositionChange, Connection as XYConnection, Edge as XYEdge, EdgeChange as XYEdgeChange, Node as XYNode, NodeChange as XYNodeChange, XYPosition } from '@xyflow/react'
import { FlowEditorMode } from '../components/flows/SettlementFlowEditor'
import affirm from '../core/Affirm'
import algo from '../core/Algo'
import { ArgumentKeyType, GroupBox, IFlow, INode, INodeInput, NodeType, OutputKeyType } from '../definitions/Nodes'
import { INodeDescription } from '../definitions/NodesDescription'
import Helper from '../helpers/Helper'
import NodeManager from './NodeManager'
import ValidationManager from './ValidationManager'

class FlowUIManagerClass {
    EDGE_ANIMATION_DURATION_MS = 2000
    NODE_WIDTH = 240
    NODE_HEIGHT = 63
    GROUP_PADDING = 28

    autoLayout = (nodes: XYNode[], edges: XYEdge[]) => {
        affirm(nodes, 'Invalid nodes')
        affirm(edges, 'Invalid edges')

        const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
        g.setGraph({ rankdir: 'TB', nodesep: 56 })

        edges.forEach(edge => g.setEdge(edge.source, edge.target))
        nodes.forEach(node =>
            g.setNode(node.id, {
                ...node,
                width: Math.max(node.measured?.width ?? 0, 150),
                height: Math.max(node.measured?.height ?? 0, 75)
            })
        )

        Dagre.layout(g)

        return {
            nodes: nodes.map(node => {
                const position = g.node(node.id)
                // We are shifting the dagre node position (anchor=center center) to the top left
                // so it matches the React Flow node anchor point (top left).
                const x = position.x - (Math.max(node.measured?.width ?? 0, 150)) / 2
                const y = position.y - (Math.max(node.measured?.height ?? 0, 75)) / 2

                return { ...node, position: { x, y } }
            }),
            edges
        }
    }

    _encodeEdge = (node: INode<NodeType>, input: INodeInput<NodeType>) => `edge:${node.uuid}__${input.uuid}__${input.outputKey.split('.')[0]}__${input.argumentKey}`

    _decodeEdge = (edgeId: string) => {
        const parts = edgeId.replace('edge:', '').split('__')

        affirm(parts.length === 4, `Invalid format for edge "${edgeId}": excpecting format of type {...}__{...}__{...}__{...}`)
        return { targetId: parts[0], sourceId: parts[1], sourceHandle: parts[2], targetHandle: parts[3] }
    }

    flowToUI = (flow: IFlow) => {
        affirm(flow, 'Invalid flow')

        const nodes: XYNode[] = []
        const edges: XYEdge[] = []

        for (let i = 0; i < flow.nodes.length; i++) {
            const node = flow.nodes[i]

            // create nodes group
            if (node.type === 'fork') {
                nodes.push({
                    id: node.group.uuid,
                    position: node.group.coords,
                    type: 'group',
                    data: {},
                    width: node.group.width,
                    height: node.group.height,
                    style: { zIndex: -2, backgroundColor: 'var(--primary-selected)', borderStyle: 'dashed', borderColor: 'var(--primary-color)' },
                    draggable: true,
                    deletable: false,
                    selectable: false
                })

                nodes.push({
                    id: node.uuid,
                    position: node.coords,
                    type: 'custom',
                    data: { node },
                    parentId: node.group.uuid
                })
            } else {
                nodes.push({
                    id: node.uuid,
                    position: node.coords,
                    type: 'custom',
                    data: { node },
                    ...(algo.hasVal(node.group?.uuid)
                        ? { parentId: node.group.uuid }
                        : {}) })
            }

            for (let j = 0; j < node.inputs.length; j++) {
                const input = node.inputs[j]
                const edgeId = this._encodeEdge(node, input)

                const { sourceHandle, sourceId, targetHandle, targetId } = this._decodeEdge(edgeId)
                edges.push({
                    id: edgeId,
                    type: 'custom',
                    source: sourceId,
                    target: targetId,
                    sourceHandle: sourceHandle,
                    targetHandle: targetHandle,
                    data: { node }
                })
            }
        }

        return { nodes, edges }
    }

    updateNode = (node: INode<NodeType>, flow: IFlow): IFlowChange => {
        affirm(node, 'Invalid node')
        affirm(flow, 'Invalid flow')

        const newFlow = structuredClone(flow)
        const index = newFlow.nodes.findIndex(n => n.uuid === node.uuid)
        affirm(index > -1, `Node to update is not found: ${node.uuid}`)

        newFlow.nodes[index] = node
        if (!node?.overwriteShape)
            newFlow.nodes[index].jsonSchema = null

        return { flow: newFlow, isUpdated: true, needsSync: true }
    }

    updateNodes = (changes: XYNodeChange<any>[], flow: IFlow): IFlowChange => {
        affirm(changes, 'Invalid changes.')
        affirm(flow, 'Invalid flow.')
        // TODO: this is cloning everytime each and every node is moved, causing shit performance ??
        const newFlow = structuredClone(flow)
        let isUpdated = false, needsSync = false

        for (let i = 0; i < changes.length; i++) {
            const change = changes[i]
            switch (change.type) {
                case 'position': {
                    const c = change as NodePositionChange
                    affirm(c.position, 'Invalid position')
                    affirm(!isNaN(c.position.x), 'Invalid x position value')
                    affirm(!isNaN(c.position.y), 'Invalid y position value')

                    if (!c.dragging) {
                        if (c.id.includes('group')) {
                            const forkNode = newFlow.nodes.find(n => n.type === 'fork' && n.group.uuid === c.id)
                            forkNode.group = { ...forkNode.group, coords: c.position }
                            newFlow.nodes = this.updateGroupPosition(newFlow.nodes, forkNode.group.uuid)
                        } else {
                            const node = newFlow.nodes.find(x => x.uuid === c.id)
                            affirm(node, 'Moved node doesn\'t exist in flow')
                            node.coords = c.position
                            if (node?.group?.uuid) newFlow.nodes = this.updateGroupPosition(newFlow.nodes, node.group.uuid)
                        }

                        isUpdated = true
                        needsSync = true
                    }
                    break
                }
                case 'remove': {
                    const index = newFlow.nodes.findIndex(node => node.uuid === change.id)
                    affirm(index > -1, `Node to remove is not found: ${change.id}`)

                    const deletedNodes = newFlow.nodes.splice(index, 1)
                    const deletedNode = deletedNodes[0]

                    for (const node of newFlow.nodes) {
                        // Update group coords if deleted node was a fork
                        if (deletedNode.type === 'fork' && node?.group?.uuid === deletedNode.group.uuid) {
                            node.group = {}
                            node.coords = {
                                x: node.coords.x + deletedNode.group.coords.x,
                                y: node.coords.y + deletedNode.group.coords.y
                            }
                        }

                        // Remove inputs referencing deleted node
                        if (node.inputs.some(x => x.uuid === change.id)) {
                            node.inputs = node.inputs.filter(input => input.uuid !== change.id)
                        }
                    }

                    isUpdated = true
                    needsSync = true
                    break
                }
                case 'select': {
                    isUpdated = false
                    needsSync = false
                    break
                }
                case 'add': {
                    const c = change as NodeAddChange
                    affirm(c.item, 'Node to add is not found')

                    const node: INode<NodeType> = (c.item as Record<string, unknown>).node as INode<NodeType>

                    if (node.type === 'fork') {
                        node.group = {
                            uuid: `group_${node.uuid}`,
                            coords: { x: node.coords.x, y: node.coords.y },
                            width: this.NODE_WIDTH + 2 * this.GROUP_PADDING,
                            height: this.NODE_HEIGHT + 2 * this.GROUP_PADDING
                        }
                        node.coords = { x: this.GROUP_PADDING, y: this.GROUP_PADDING }
                    }

                    newFlow.nodes.push(c.item['node'])
                    isUpdated = true
                    needsSync = true
                    break
                }
                case 'replace':
                case 'dimensions' : {
                    // TODO: Motivate the reason for (false, false)
                    isUpdated = false
                    needsSync = false
                    break
                }
                default: {
                    // This is important (for instance for "selected") or it breaks some functionalities
                    isUpdated = true
                    needsSync = false
                    break
                }
            }
        }

        return { flow: newFlow, isUpdated, needsSync }
    }

    updateConnections = (changes: XYEdgeChange<XYEdge>[], flow: IFlow): IFlowChange => {
        affirm(changes, 'Invalid changes.')
        affirm(flow, 'Invalid flow.')
        const newFlow = structuredClone(flow)
        let isUpdated = false, needsSync = false

        for (let i = 0; i < changes.length; i++) {
            const change = changes[i]
            switch (change.type) {
                case 'remove': {
                    const { sourceId, targetId, sourceHandle, targetHandle } = this._decodeEdge(change.id)
                    affirm(sourceId, 'Invalid source id')
                    affirm(targetId, 'Invalid target id')
                    affirm(sourceHandle, 'Invalid source handle')
                    affirm(targetHandle, 'Invalid target handle')

                    const source = newFlow.nodes.find(x => x.uuid === sourceId)
                    const target = newFlow.nodes.find(x => x.uuid === targetId)
                    affirm(source, `Source node "${sourceId}" doesn't exist`)
                    affirm(target, `Target node "${targetId}" doesn't exist`)

                    const inputIndex = target.inputs.findIndex(x => x.uuid === sourceId && x.argumentKey === targetHandle && x.outputKey === sourceHandle)
                    affirm(inputIndex > -1, `The required input for deletion doesn't exist`)

                    // Handle group nodes
                    if (source.group?.uuid && target.group?.uuid) {
                        if (source.type === 'fork') {
                            this.updateNodesInGroup(target, source.group, newFlow.nodes, 'remove')
                            this.updateGroupPosition(newFlow.nodes, source.group.uuid)
                        } else {
                            if (target.type !== 'end_fork') {
                                this.updateNodesInGroup(target, source.group, newFlow.nodes, 'remove')
                                this.updateGroupPosition(newFlow.nodes, source.group.uuid)
                            } else {
                                const targetInput = newFlow.nodes.find(n => n.uuid === target.uuid)
                                if (targetInput.inputs.length === 1) {
                                    this.updateNodesInGroup(target, source.group, newFlow.nodes, 'remove')
                                    this.updateGroupPosition(newFlow.nodes, source.group.uuid)
                                }
                            }
                        }
                    }

                    target.inputs.splice(inputIndex, 1)

                    isUpdated = true
                    needsSync = true
                    break
                }
                case 'select': {
                    isUpdated = false
                    needsSync = false
                    break
                }
                case 'add': {
                    isUpdated = true
                    needsSync = true
                    break
                }
                default: {
                    // This is important (for instance for "selected") or it breaks some functionalities
                    isUpdated = true
                    needsSync = true
                    break
                }
            }
        }
        return { flow: newFlow, isUpdated, needsSync }
    }

    createConnection = (params: XYEdge | XYConnection, flow: IFlow): IFlowChange => {
        affirm(params, 'Invalid @params passed to create connection')
        affirm(params.source, 'Invalid @params source passed to create connection')
        affirm(params.target, 'Invalid @params target passed to create connection')
        affirm(flow, 'Invalid @flow passed to create connection')

        const newFlow = structuredClone(flow)
        const source = newFlow.nodes.find(x => x.uuid === params.source)
        const target = newFlow.nodes.find(x => x.uuid === params.target)

        affirm(source, `Source node ${params.source} doesn't exist in flow`)
        affirm(target, `Target node ${params.target} doesn't exist in flow`)

        const errors = ValidationManager.validateNewConnection(source, target, params.sourceHandle, params.targetHandle, flow)
        if (errors.length > 0) throw new Error(`Connection error:\n${errors.join(';\n')}`)

        // Handle groups of nodes
        if (source.group?.uuid && source.type !== 'end_fork') {
            if (target.type !== 'fork') {
                // this need to reorder the flow placing fork before its children
                if (source.type === 'fork') {
                    const children = newFlow.nodes.filter(node => node.group?.uuid === source.group?.uuid && node.uuid !== source.uuid)
                    const nodesToReorder = [source, ...children]
                    newFlow.nodes = newFlow.nodes.filter(node => !nodesToReorder.includes(node))
                    newFlow.nodes.unshift(...nodesToReorder)
                }
                this.updateNodesInGroup(target, source.group, newFlow.nodes, 'add')
                this.updateGroupPosition(newFlow.nodes, source.group.uuid)
            }
        }

        const newInput: INodeInput = {
            uuid: source.uuid,
            outputKey: params.sourceHandle as OutputKeyType<typeof target.type>,
            argumentKey: params.targetHandle as ArgumentKeyType<typeof target.type>
        }

        target.inputs.push(newInput)

        // Here I'm doing something not ideal... I'm overwriting the id on params so that I can enforce a format on edges id
        // The format must be enforced because I then need to be able to decode the format to know the nodes that are connected by the edge (e.g. when I'm deleting an edge, detaching two nodes)
        // If I don't force it, then xyflow uses another standard that I can't work with
        // @ts-expect-error I'm forcing the id as explained above
        params.id = this._encodeEdge(target, newInput)

        return { flow: newFlow, isUpdated: true, needsSync: true }
    }

    createNode = (type: NodeType, position: XYPosition, flow: IFlow): IFlowChange & { newNode: INode } => {
        affirm(type, 'Invalid type')
        affirm(position, 'Invalid coordinates')
        affirm(flow, 'Invalid flow')
        affirm(flow.nodes, 'Invalid nodes in flow')

        const description = NodeManager.getDescription(type)
        affirm(description, 'Invalid nodes description')

        const defaultValue = {}

        for (let i = 0; i < description.acceptedData.length; i++) {
            const acceptedData = description.acceptedData[i]
            if (acceptedData.defaultValue) {
                defaultValue[acceptedData.parameter as string] = acceptedData.defaultValue
            }
        }

        const nodeName = this.getNodeName(flow.nodes, `New node ${type}`)

        const newNode: INode = {
            uuid: Helper.generateUUID(),
            coords: position,
            name: nodeName,
            type: type,
            data: defaultValue,
            inputs: []
        }

        if (newNode.type === 'xfrm_sql') {
            newNode.dryRunConfig = { samplePercentage: 100 }
        }

        const newFlow = structuredClone(flow)
        newFlow.nodes.push(newNode)

        return { flow: newFlow, newNode, isUpdated: true, needsSync: true }
    }

    duplicateNodes = (nodes: INode[], flow: IFlow): IFlowChange => {
        affirm(nodes.length > 0, 'No nodes to duplicate')
        affirm(flow, 'Flow not valid')

        const updatedFlow = structuredClone(flow)
        const newNodes: INode[] = []

        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i]
            const position = { x: node.coords.x, y: node.coords.y + 150 }
            const { newNode: newNode } = this.createNode(node.type, position, updatedFlow)
            newNode.data = node.data
            if (newNode.type === 'fork') {
                newNode.group = {
                    uuid: `group_${newNode.uuid}`,
                    coords: { x: newNode.coords.x, y: newNode.coords.y },
                    width: this.NODE_WIDTH + this.GROUP_PADDING,
                    height: this.NODE_HEIGHT + this.GROUP_PADDING
                }
                newNode.coords = { x: this.GROUP_PADDING / 2, y: this.GROUP_PADDING / 2 }
            }
            newNodes.push(newNode)
            updatedFlow.nodes.push(newNode)
        }

        return { flow: updatedFlow, isUpdated: true, needsSync: true }
    }

    filterTypeNodes = (group: INodeDescription<NodeType>[], flow: IFlow): INodeDescription<NodeType>[] => {
        const isSubFlow = flow.type === 'sub-flow'
        let filteredGroup = group

        if (isSubFlow) {
            const nodeToRemoveSubFlow: NodeType[] = ['start']
            filteredGroup = filteredGroup.filter(x => !nodeToRemoveSubFlow.includes(x.type))
        } else {
            const nodeToRemoveFlow: NodeType[] = ['subFlow_start', 'subFlow_end']
            filteredGroup = filteredGroup.filter(x => !nodeToRemoveFlow.includes(x.type))
        }

        if (!Helper.isDev()) {
            const nodeToRemoveDev: NodeType[] = ['dummy', 'dummy2']
            filteredGroup = filteredGroup.filter(x => !nodeToRemoveDev.includes(x.type))
        }

        return filteredGroup
    }

    getActionBarColor = (editorMode: FlowEditorMode, runMode: 'dry' | 'full', flow: IFlow): OverridableStringUnion< 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning', ButtonPropsColorOverrides > => {
        const isSubFlow = flow?.type === 'sub-flow'

        switch (editorMode) {
            case 'edit':
            case 'run':
            case 'dry-run': {
                if (runMode === 'dry')
                    return 'dryRun'
                if (isSubFlow)
                    return 'subFlow'
                else
                    return 'primary'
            }
            case 'audit':
            case 'compare': return 'audit'
            default: return 'primary'
        }
    }

    getNodeName = (nodes: INode[], name: string) => {
        affirm(nodes, 'No nodes provided')
        affirm(name, 'No node name provided')

        let counter = 1
        let nodeName = name

        while (nodes.find(n => n.name === nodeName) && counter < 99) {
            nodeName = `${name} (${counter})`
            counter += 1
        }
        return nodeName
    }

    isUniqueName = (flow: IFlow, selected: INode<NodeType>) => {
        let isGlobals = false
        if (selected.name.toLowerCase() === 'globals') isGlobals = true
        return flow.nodes.some(node => (node.name?.trim() === selected.name?.trim() && node.uuid !== selected.uuid) || isGlobals)
    }

    getNodeColor = (nodeType: NodeType) => {
        switch (nodeType) {
            case 'xfrm_sql': return colors.purple[300]
            case 'var_dataframe': return colors.amber[300]
            case 'xfrm_reduce': return colors.orange[600]
            case 'xfrm_dataframeops': return colors.orange[300]
            case 'xfrm_code': return colors.cyan[300]
            case 'out_csv': return colors.green[300]
            case 'stmt_settlement': return colors.lime[300]
            case 'ds_redshift':
            case 'ds_dynamo': return colors.indigo[300]
            case 'var_variable': return colors.blue[300]
            default: return 'var(--icon-color)'
        }
    }

    updateGroupPosition = (nodes: INode<NodeType>[], groupId: string): INode<NodeType>[] => {
        const nodesInGroup = nodes.filter(x => x?.group?.uuid === groupId)
        const groupCoords = nodesInGroup.find(n => n.group.uuid === groupId).group.coords

        const minX = Math.min(...nodesInGroup.map(node => node.coords.x))
        const minY = Math.min(...nodesInGroup.map(node => node.coords.y))

        const maxX = Math.max(...nodesInGroup.map(node => node.coords.x + this.NODE_WIDTH))
        const maxY = Math.max(...nodesInGroup.map(node => node.coords.y + this.NODE_HEIGHT))

        nodesInGroup.forEach(node => {
            node.coords = { x: node.coords.x - minX + this.GROUP_PADDING, y: node.coords.y - minY + this.GROUP_PADDING }
            node.group.coords = { x: groupCoords.x + minX - this.GROUP_PADDING, y: groupCoords.y + minY - this.GROUP_PADDING }
            node.group.width = maxX - minX + 2 * this.GROUP_PADDING
            node.group.height = maxY - minY + 2 * this.GROUP_PADDING
        })

        return nodes.map(node => {
            const updatedNode = nodesInGroup.find(n => n.uuid === node.uuid)
            return updatedNode ? updatedNode : node
        })
    }

    updateNodesInGroup = (startNode: INode<NodeType>, group: GroupBox, nodes: INode<NodeType>[], action: 'add' | 'remove'): INode<NodeType>[] => {
        const updatedNodes = nodes

        const traverseGroup = (currNode: INode<NodeType>) => {
            if (action === 'remove') {
                if (currNode?.group?.uuid === group.uuid) {
                    if (currNode.type === 'end_fork') {
                        const targetInput = updatedNodes.find(n => n.uuid === currNode.uuid)
                        if (targetInput.inputs.length !== 1) return
                    }
                    currNode.group = {}
                    currNode.coords = {
                        x: group.coords.x + currNode.coords.x,
                        y: group.coords.y + currNode.coords.y
                    }
                }
            } else if (action === 'add') {
                if (currNode.type === 'fork' || updatedNodes.some(n => currNode.inputs.some(i => i.uuid === n.uuid && n.type === 'end_fork'))) return

                if (!currNode.group || currNode.group.uuid !== group.uuid) {
                    currNode.group = {
                        ...currNode.group,
                        uuid: group.uuid,
                        coords: { x: group.coords.x, y: group.coords.y }
                    }
                    currNode.coords = {
                        x: currNode.coords.x - group.coords.x,
                        y: currNode.coords.y - group.coords.y
                    }
                }
            }

            const children = updatedNodes.filter(node =>
                node.inputs.some(input => input.uuid === currNode.uuid)
            )

            children.forEach(child => traverseGroup(child))
        }

        const startNodeInUpdatedNodes = updatedNodes.find(node => node.uuid === startNode.uuid)
        if (startNodeInUpdatedNodes) {
            traverseGroup(startNodeInUpdatedNodes)
        }

        return updatedNodes
    }
}

const FlowUIManager = new FlowUIManagerClass()
export default FlowUIManager

export interface IFlowChange {
    /**
     * The new flow if something changed
     */
    flow: IFlow
    /**
     * Whether something changed or not
     */
    isUpdated: boolean
    /**
     * Whether it needs to be synced with the backend (API call)
     */
    needsSync: boolean
}
